<script setup lang="ts">
import type { SelectFilter, SelectGroupOption, SelectInst } from 'naive-ui'
import type { ProjectOption } from './project-option.type'
import type { Project } from '~/types/project.type'
import { ProjectSelectOption } from '#components'
import { breakpointsTailwind } from '@vueuse/core'

const props = defineProps({
  projects: {
    type: Array as PropType<Array<Project<'withClient'>>>,
    default: () => [],
  },
  showRecentProjectsGroup: {
    type: Boolean,
    default: false,
  },
})

const project = defineModel<Project['id'] | null>({ default: undefined })
const recent = ref(false)

const selectModel = computed<string | Project['id'] | null>({
  get() {
    return recent.value ? `recent-${project.value}` : project.value
  },
  set(projectId) {
    if (typeof projectId === 'string') {
      recent.value = true
      project.value = Number.parseInt(projectId.replace('recent-', ''))
    }
    else {
      recent.value = false
      project.value = projectId
    }
  },
})

interface ProjectGroupAccumulator { [clientId: number]: SelectGroupOption & { children: ProjectOption[] } }

const projectOptions = computed(() => {
  const initialGroups: Array<SelectGroupOption & { children: ProjectOption[] }> = []

  if (props.showRecentProjectsGroup) {
    initialGroups.push({
      key: 'recent',
      type: 'group',
      label: 'Zuletzt verwendet',
      children: props.projects.slice(0, 5).map<ProjectOption>((project) => {
        return {
          key: `recent-${project.id}`,
          label: `${project.name} (${project.client.name})`,
          value: `recent-${project.id}`,
          clientName: project.client.name,
          project,
          class: 'project-option',
        }
      }),
    })
  }

  return initialGroups.concat(Object.values(props.projects.reduce((acc, project) => {
    if (!acc[project.client.id]) {
      acc[project.client.id] = {
        type: 'group',
        label: project.client.name,
        key: `client-${project.client.id}`,
        children: [],
      }
    }

    acc[project.client.id].children?.push({
      label: project.name,
      value: project.id,
      clientName: project.client.name,
      project,
      class: 'project-option',
    })

    return acc
  }, {} as ProjectGroupAccumulator)))
})

const filter: SelectFilter = (pattern, option) => {
  const projectOption = option as ProjectOption
  const label = projectOption.label
  if (typeof label === 'string')
    return patternMatched(pattern, label) || patternMatched(pattern, projectOption.clientName)

  return false
}

const select = ref<SelectInst>()

function renderProjectOptionLabel(option: ProjectOption | SelectGroupOption) {
  return h(ProjectSelectOption, { option })
}

onMounted(() => nextTick(() => select.value?.focus()))

const { md } = useBreakpoints(breakpointsTailwind)
</script>

<template>
  <NSelect
    ref="select"
    v-model:value="selectModel"
    :options="projectOptions"
    placeholder="Projekt auswählen"
    filterable
    :filter="filter"
    :render-label="renderProjectOptionLabel"
    :consistent-menu-width="!md"
    class="group/select"
  />
</template>

<style scoped>
:global(.project-option .n-base-select-option__content) {
  @apply w-full;
}

.n-select :deep(.copy-button) {
  @apply hidden;
}
</style>
